import React from 'react';
import { Link } from 'react-router-dom';
import { useNearContext } from '../contexts/NearContext';
import { useActiveWagers, getWagerIdentifier } from '../hooks/useWagers';
import LoadingSpinner from './LoadingSpinner';

const Home = () => {
  const { isSignedIn, formatNearAmount } = useNearContext();
  const {
    data: wagers = [],
    isLoading,
    isError,
    refetch,
  } = useActiveWagers({ limit: 12 });

  return (
    <div className="space-y-8">
      <header className="text-center space-y-4">
        <p className="inline-flex items-center gap-2 px-4 py-1 rounded-full text-sm font-medium bg-blue-100 text-near-blue">
          NearBets v2 • Smart-contract enforced wagers on NEAR
        </p>
        <h1 className="text-4xl font-bold text-gradient">
          Decentralized P2P Betting
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Spin up wagers with friends, settle outcomes on-chain, and tap into prop bets or micro markets—all powered by the NearBets smart contract.
        </p>
      </header>

      {!isSignedIn && (
        <div className="card max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-semibold mb-3">Ready to place a wager?</h2>
          <p className="text-gray-600 mb-4">
            Connect your NEAR wallet to create wagers, join existing markets, and track your winnings in real time.
          </p>
          <p className="text-sm text-gray-400">
            Looking for the wallet button? You&apos;ll find <span className="font-semibold text-gray-600">Connect Wallet</span> in the top-right of the header.
          </p>
        </div>
      )}

      <section className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold">Active Wagers</h2>
          <button
            onClick={refetch}
            className="btn-secondary text-sm"
            type="button"
          >
            Refresh
          </button>
        </div>

        {isLoading && (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        )}

        {isError && (
          <div className="text-center text-red-500 py-8">
            <p className="mb-4 font-medium">We couldn&apos;t reach the contract right now.</p>
            <button type="button" onClick={refetch} className="btn-primary">
              Try Again
            </button>
          </div>
        )}

        {!isLoading && !isError && wagers.length === 0 && (
          <div className="text-center text-gray-500 py-12">
            <p className="font-medium">No wagers are live yet.</p>
            <p className="text-sm mt-2">Be the first to create one and invite friends to join.</p>
          </div>
        )}

        {!isLoading && !isError && wagers.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {wagers.map((wager) => {
              const wagerId = getWagerIdentifier(wager);
              if (!wagerId && wagerId !== 0) {
                return null;
              }

              const totalStake = formatNearAmount(wager.total_stake || '0');
              const minStake = formatNearAmount(wager.request?.min_stake || '0');
              const participantCount = Array.isArray(wager.participants)
                ? wager.participants.length
                : 0;
              const choices = Array.isArray(wager.request?.choices)
                ? wager.request.choices
                : [];
              const highlightedChoices = choices.slice(0, 3).join(' • ');

              return (
                <article key={wagerId} className="border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-lg transition-shadow duration-200 flex flex-col gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs uppercase tracking-wide">
                      <span className="font-semibold text-near-blue">On-chain wager</span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-600 uppercase text-[11px] font-semibold">
                        {wager.status}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {wager.request?.title ?? 'Untitled wager'}
                    </h3>
                    <p className="text-sm text-gray-500 max-h-20 overflow-hidden">
                      {wager.request?.description ?? 'No description provided.'}
                    </p>
                    {highlightedChoices && (
                      <p className="text-xs uppercase tracking-wide text-gray-400">
                        Options: {highlightedChoices}
                      </p>
                    )}
                  </div>

                  <dl className="grid grid-cols-3 gap-3 text-sm">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <dt className="text-gray-500">Pool</dt>
                      <dd className="font-semibold text-gray-800">{totalStake} NEAR</dd>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <dt className="text-gray-500">Min Stake</dt>
                      <dd className="font-semibold text-gray-800">{minStake} NEAR</dd>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <dt className="text-gray-500">Players</dt>
                      <dd className="font-semibold text-gray-800">{participantCount}</dd>
                    </div>
                  </dl>

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-400 uppercase tracking-wide">
                      Creator: {wager.creator}
                    </div>
                    <Link to={`/wager/${wagerId}`} className="btn-primary text-sm">
                      View wager
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;
